import * as React from 'react';
import axios from 'axios';
import { isEqual } from 'lodash-es';
import { Dialog } from 'primereact/dialog';
import { useDispatch } from 'react-redux';
import { DataPager } from '../../../components/datapagers/DataPager';
import { NewButton } from '../../../components/synced/ui/NewButton/NewButton';
import { constantTable } from '../../../helpers/constants';
import { handleError } from '../../../helpers/hooks/handleError';
import { services } from '../../../services';
import { NoteUpdatesController } from '../helpers/noteUpdates';
import { readNotePage } from '../helpers/notePageUpdates';
import { useNotePageUpdates } from '../hooks/useNotePageUpdates';
import { PDFNoteQuery } from '../models/PDFNoteQuery';
import { PDFNoteRefreshPageResponse } from '../models/PDFNoteRefreshPage';
import { NoteCard } from './NoteCard';
import { NoteForm } from './NoteForm';
import { NotesFilters } from './NotesFilters';
import type { NotesPagerProps } from './NotesPager';

type NotesPagerWithUpdatesProps = NotesPagerProps & { noteUpdates: NoteUpdatesController };

const emptyPage: PDFNoteRefreshPageResponse = {
  Items: [], Actions: [], PageIds: [], TotalCount: 0, Skipped: 0, Take: constantTable.paginatorRows,
};

export const NotesPagerWithUpdates: React.FC<NotesPagerWithUpdatesProps> = ({
  dataStorage, initialQueryName, initialQuery, projectDocFolderStatusId,
  projectDoc, requestEnable, onNoteChanged, noteUpdates, paging,
}) => {
  const dispatch = useDispatch();
  const resultKey = `${initialQueryName}_NotesPager_taskNotes`;
  const queryKey = `${initialQueryName}_NotesPager_taskNotesQuery`;
  const [query, setQuery] = React.useState<PDFNoteQuery>(() => ({
    ...dataStorage.get<PDFNoteQuery>(queryKey, initialQuery),
    ...(paging ? { Skip: paging.offset, Take: constantTable.paginatorRows } : {}),
    NoteObjectIds: initialQuery.NoteObjectIds,
    StatusIds: initialQuery.StatusIds,
    RequireCompletedTask: initialQuery.RequireCompletedTask,
    IsDeleted: false,
    IncludeDeletedWorkflowInstances: false,
  }));
  const desiredQuery = React.useRef(query);
  const pagingRef = React.useRef(paging);
  React.useLayoutEffect(() => { pagingRef.current = paging; });
  const [page, setPage] = React.useState<PDFNoteRefreshPageResponse>(emptyPage);
  const [initialLoading, setInitialLoading] = React.useState(requestEnable);
  const [refreshLoading, setRefreshLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState<'initial' | 'refresh' | null>(null);
  const [initialRetry, setInitialRetry] = React.useState(0);
  const [formNoteId, setFormNoteId] = React.useState<number | null>(null);

  const storeQuery = React.useCallback((nextQuery: PDFNoteQuery) => {
    dataStorage.set(queryKey, nextQuery);
    dataStorage.set(initialQueryName, nextQuery);
    dataStorage.set('NotesPager_Skip', nextQuery.Skip);
  }, [dataStorage, queryKey, initialQueryName]);

  const applyPage = React.useCallback((nextPage: PDFNoteRefreshPageResponse, nextQuery: PDFNoteQuery, persistPaging = true) => {
    desiredQuery.current = nextQuery;
    dataStorage.set(resultKey, nextPage);
    storeQuery(nextQuery);
    setPage(nextPage);
    setQuery(current => isEqual(current, nextQuery) ? current : nextQuery);
    setLoadError(null);
    if (persistPaging) pagingRef.current?.onOffsetChange(nextQuery.Skip ?? 0, true);
  }, [dataStorage, resultKey, storeQuery]);

  const pageUpdates = useNotePageUpdates({
    noteUpdates,
    query,
    enabled: requestEnable,
    request: async request => {
      dataStorage.set(resultKey, undefined);
      setRefreshLoading(true);
      setLoadError(null);
      try {
        return (await services.pDFNoteService.refreshPage(request)).data;
      } finally {
        setRefreshLoading(false);
      }
    },
    onPage: applyPage,
    onError: error => {
      setLoadError('refresh');
      handleError(error, dispatch);
    },
  });

  const updateQuery = React.useCallback((changes: Partial<PDFNoteQuery>, persistPaging = true) => {
    const next = {
      ...desiredQuery.current,
      ...changes,
      ...(pagingRef.current ? { Take: constantTable.paginatorRows } : {}),
      IsDeleted: false,
      IncludeDeletedWorkflowInstances: false,
    };
    if (isEqual(desiredQuery.current, next)) return;
    const offsetChanged = desiredQuery.current.Skip !== next.Skip;
    desiredQuery.current = next;
    // Ответ прежнего запроса может прийти до следующего commit React.
    pageUpdates.setQuery(next);
    setQuery(next);
    if (offsetChanged && persistPaging) pagingRef.current?.onOffsetChange(next.Skip ?? 0);
  }, [pageUpdates]);

  React.useLayoutEffect(() => {
    updateQuery(pagingRef.current ? { ...initialQuery, Skip: pagingRef.current.offset } : initialQuery, false);
  }, [initialQuery, updateQuery]);

  React.useLayoutEffect(() => {
    if (paging) updateQuery({ Skip: paging.offset }, false);
  }, [paging?.offset, updateQuery]);

  React.useLayoutEffect(() => {
    if (!requestEnable) {
      setPage(emptyPage);
      setInitialLoading(false);
      return;
    }
    if (!isEqual(desiredQuery.current, query)) return;
    if (pageUpdates.consumeQueryEcho(query)) return;
    const token = pageUpdates.beginLoad(query);
    // очищаем прежние карточки до отрисовки и подтверждаем состав страницы на сервере
    setPage(emptyPage);
    dataStorage.set(resultKey, undefined);

    let active = true;
    const isCurrent = () => active && isEqual(desiredQuery.current, query);
    const cancellation = axios.CancelToken.source();
    setInitialLoading(true);
    setLoadError(null);
    readNotePage(
      { Query: query, KnownIds: [], ChangedIds: [] },
      request => services.pDFNoteService.refreshPage(request, cancellation.token).then(response => response.data),
      undefined,
      isCurrent,
    )
      .then(nextPage => {
        if (!nextPage || !pageUpdates.acceptPage(token, nextPage)) return;
        setInitialLoading(false);
        applyPage(nextPage, { ...query, Skip: nextPage.Skipped, Take: nextPage.Take });
      })
      .catch(error => {
        if (!isCurrent() || axios.isCancel(error)) return;
        setLoadError('initial');
        handleError(error, dispatch);
      })
      .finally(() => { if (isCurrent()) setInitialLoading(false); });
    return () => {
      active = false;
      cancellation.cancel();
    };
  }, [query, requestEnable, pageUpdates, dataStorage, queryKey, resultKey, initialRetry, applyPage, dispatch]);

  const loading = initialLoading || refreshLoading;
  return (
    <>
      <Dialog
        header="Просмотр замечания"
        onHide={() => setFormNoteId(null)}
        visible={formNoteId !== null}
        maximized
        className="note-view-dialog"
        modal
        closable>
        <NoteForm id={formNoteId ?? 0} projectDoc={projectDoc} onNoteChanged={() => { }} />
      </Dialog>
      <div className="notes-list">
        <NotesFilters
          codeFilterId={query.CodeIds[0] ?? 0}
          onCodeFilterChange={codeId => updateQuery({ CodeIds: codeId > 0 ? [codeId] : [] })}
          projectDocTypeId={projectDoc?.DocTypeId}
          totalCount={page.TotalCount} />
        {loadError && (
          <div role="alert">
            Не удалось обновить замечания. Повторите загрузку.
            <NewButton
              variant="secondary"
              disabled={loading}
              onClick={() => {
                if (loadError === 'initial') setInitialRetry(value => value + 1);
                else pageUpdates.retry();
              }}>
              Повторить
            </NewButton>
          </div>
        )}
        <DataPager
          data={page}
          query={query}
          updateQuery={updateQuery}
          loading={loading}
          itemTemplate={({ item }) => (
            <NoteCard
              pdfNote={item}
              projectDocFolderStatusId={projectDocFolderStatusId}
              projectDoc={projectDoc}
              onNoteChanged={onNoteChanged}
              onFormLinkClick={() => setFormNoteId(item.Id ?? 0)}
              noteUpdates={noteUpdates}
              noteUpdatesPending={loading || loadError !== null} />
          )} />
      </div>
    </>
  );
};
